import React, { useState, useRef } from 'react';
import './DeckBuilder.css';

import DeckSaveLoad from './DeckSaveLoad';

function DeckBuilder({ deck, removeCardFromDeck, showCardDetails, cardTypeOptions, elementOptions, subElementOptions, powerOptions, corpsOptions, levelOptions, sexOptions, plusPowerTargetOptions, plusPowerOptions, chapterOptions, continentOptions, onLoadDeck, openModal, userInfo, reloadTrigger, setReloadTrigger }) {

  const pressTimer = useRef(null);

  // 長押し開始
  const handleTouchStart = (card) => {
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      // 🔔 振動（対応端末のみ）
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      removeCardFromDeck(card.card_id);
    }, 500); // 500ms 長押し
  };

  // 指を離したらキャンセル
  const handleTouchEnd = () => {
    clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  // 指を動かしたらキャンセル（スクロール対策）
  const handleTouchMove = () => {
    clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  // 任意の項目を集計する汎用関数
  const countByKey = (deck, key) => {
    return deck.reduce((acc, card) => {
      const value = card[key];
      if (value != null) {
        acc[value] = (acc[value] || 0) + 1;
      }
      return acc;
    }, {});
  };

  // 任意のコード → 名前に変換
  const getLabel = (options, codeKey, labelKey, value) => {
    const found = options.find(opt => String(opt[codeKey]) === String(value));
    return found ? found[labelKey] : '不明';
  };

  // ソート選択状態
  const [sortPattern, setSortPattern] = useState('element');

  // ソート関数
  const sortMainDeck = (cards) => {
    const getSortKey = (card, key) => card[key] ?? '';

    const compare = (a, b, keys) => {
      for (const key of keys) {
        const valA = a[key];
        const valB = b[key];
        // null/undefined を後ろに回す
        if (valA == null && valB != null) return 1;
        if (valA != null && valB == null) return -1;
        if (valA == null && valB == null) continue;
        // 通常の比較（数値と文字列の両方に対応）
        const diff = valA.toString().localeCompare(valB.toString(), undefined, { numeric: true });
        if (diff !== 0) return diff;
      }
      return 0;
    };

    if (sortPattern === 'element') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'element_code', 'power_code']));
    } else if (sortPattern === 'power') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'power_code', 'element_code']));
    } else if (sortPattern === 'subelement') {
      return [...cards].sort((a, b) => compare(a, b, ['card_type_code', 'sub_element_code', 'element_code', 'power_code']));
    } else if (sortPattern === 'cardno') {
      return [...cards].sort((a, b) => compare(a, b, ['card_id']));
    }
    return cards;
  };


  return (
    <div>
      <DeckSaveLoad deck={deck} onLoadDeck={onLoadDeck} openModal={openModal} continentOptions={continentOptions} userInfo={userInfo} reloadTrigger={reloadTrigger} setReloadTrigger={setReloadTrigger} cardTypeOptions={cardTypeOptions} levelOptions={levelOptions} sexOptions={sexOptions} elementOptions={elementOptions} subElementOptions={subElementOptions} powerOptions={powerOptions} />
      <h3>構築中デッキ</h3>
      {/* サマリー */}
      <div className="deck-summary-grid">
        {[
          { title: 'カード分類', key: 'card_type_code', options: cardTypeOptions, codeKey: 'card_type_code', labelKey: 'card_type_name' },
          { title: '召喚師レベル', key: 'level_code', options: levelOptions, codeKey: 'level_code', labelKey: 'level' },
          { title: '性別', key: 'sex_code', options: sexOptions, codeKey: 'sex_code', labelKey: 'sex' },
          { title: '属性', key: 'element_code', options: elementOptions, codeKey: 'element_code', labelKey: 'element_name', onlyTypeCode2: true },
          { title: 'サブ属性', key: 'sub_element_code', options: subElementOptions, codeKey: 'element_code', labelKey: 'element_name' },
          { title: 'パワー', key: 'power_code', options: powerOptions, codeKey: 'power_code', labelKey: 'power' },
        ].map(({ title, key, options, codeKey, labelKey, onlyTypeCode2 }) => {
          const targetDeck = onlyTypeCode2 ? deck.filter(card => card.card_type_code === 2) : deck;
          const counts = countByKey(targetDeck, key);
          if (Object.keys(counts).length === 0) return null;

          return (
            <div className="summary-category" key={key}>
              <h4>{title}</h4>
              <div className="summary-items">
                {Object.entries(counts)
                  .sort(([a], [b]) => a - b)
                  .map(([code, count]) => (
                    <div key={code} className="summary-item">
                      <span className="item-label">
                        {getLabel(options, codeKey, labelKey, code)}
                      </span>
                      <span className="item-count">{count}</span>
                    </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* デッキリスト */}
      {/* ソート */}
      <div className="sort-selector">
        <label>ゾーン並び順:</label>
        <select value={sortPattern} onChange={(e) => setSortPattern(e.target.value)}>
          <option value="element">属性</option>
          <option value="power">パワー</option>
          <option value="subelement">サブ属性</option>
          <option value="cardno">カードNo.</option>
        </select>
      </div>
      {/* 召喚師 */}
      {deck.some(card => card.card_type_code === 1) && (
        <>
          <ul className="deck-list">
            {deck
              .filter(card => card.card_type_code === 1)
              .sort((a, b) =>
                a.level_code !== b.level_code
                  ? a.level_code - b.level_code
                  : a.card_id.localeCompare(b.card_id)
              )
              .map((card, index) => (
                <li key={`type1-${index}`}>
                  <img className="card-image"
                    src={`${process.env.PUBLIC_URL}/assets/${card.card_image}`} alt={card.card_name}
                    onDoubleClick={() => removeCardFromDeck(card.card_id)}
                    onClick={() => showCardDetails(card)}
                    onTouchStart={() => handleTouchStart(card)}  // 長押し開始
                    onTouchEnd={handleTouchEnd}      // 離したら判定
                    onTouchMove={handleTouchMove}   // スクロール中は無効
                    onContextMenu={(e) => e.preventDefault()} // ブラウザのデフォルト動作廃止
                  />
                </li>
            ))}
          </ul>
        </>
      )}
      {/* フィールド or 占星術 */}
      {deck.some(card => card.card_type_code === 8 || card.card_type_code === 9) && (
        <>
          <ul className="deck-list">
            {deck
              .filter(card => card.card_type_code === 8 || card.card_type_code === 9)
              .sort((a, b) =>
                a.card_id.localeCompare(b.card_id)
              )
              .map((card, index) => (
                <li key={`type1-${index}`}>
                  <img className="card-image"
                    src={`${process.env.PUBLIC_URL}/assets/${card.card_image}`} alt={card.card_name}
                    onDoubleClick={() => removeCardFromDeck(card.card_id)}
                    onClick={() => showCardDetails(card)}
                    onTouchStart={() => handleTouchStart(card)}  // 長押し開始
                    onTouchEnd={handleTouchEnd}      // 離したら判定
                    onTouchMove={handleTouchMove}   // スクロール中は無効
                    onContextMenu={(e) => e.preventDefault()} // ブラウザのデフォルト動作廃止
                  />
                </li>
            ))}
          </ul>
        </>
      )}
      {/* メイン */}
      <ul className="deck-list">
        {sortMainDeck(deck.filter(card => card.card_type_code !== 1 && card.card_type_code !== 8 && card.card_type_code !== 9 )).map((card, index) => (
          <li key={index}>
            <img className="card-image"
              src={`${process.env.PUBLIC_URL}/assets/${card.card_image}`} alt={card.card_name}
              onDoubleClick={() => removeCardFromDeck(card.card_id)}
              onClick={() => showCardDetails(card)}
              onTouchStart={() => handleTouchStart(card)}  // 長押し開始
              onTouchEnd={handleTouchEnd}      // 離したら判定
              onTouchMove={handleTouchMove}   // スクロール中は無効
              onContextMenu={(e) => e.preventDefault()} // ブラウザのデフォルト動作廃止
            />
          </li>
        ))}
      </ul>
      {/* デッキが空の場合のフォールバック表示 */}
      {deck.length === 0 && (
        <p style={{ marginTop: '20px', color: '#666' }}>カードが選択されていません。「カード検索」からカードを追加してください。</p>
      )}

    </div>
  );
}

export default DeckBuilder;